/**
 * @description wrapper type for creating lists types with pagination
 * @example
 * ```tsx
 *  export type SubjectListDTO = PaginationType<PopulatedSubjectDTO>
 * ```
 * @return
 * ```tsx
 *  items: T[];
 *  totalItems: number;
 *  totalPages: number;
 *  page: number;
 *  hasPrevPage: boolean;
 *  hasNextPage: boolean;
 * ```
 */

import { z } from 'zod';

export interface PaginationType<T> {
  items: T[];
  page: number;
  totalItems: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  totalPages: number;
}

export const PaginationSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    items: z.array(itemSchema),
    totalItems: z.number(),
    totalPages: z.number(),
    page: z.number(),
    hasPrevPage: z.boolean(),
    hasNextPage: z.boolean(),
  });

/**
 * Zod schema for pagination params.
 */
export const PaginationParamsSchema = (filtersSchema: z.ZodTypeAny = z.record(z.string(), z.unknown())) =>
  z.object({
    page: z.number().optional(),
    limit: z.number().optional(),
    filters: filtersSchema.or(z.record(z.string(), z.unknown())).optional(),
    sort: z.record(z.string(), z.unknown()).optional(),
  });

/**
 * Helper type to infer params from a specific PaginationParamsSchema instance.
 */
export type InferPaginationParams<T extends z.ZodTypeAny> = z.infer<T>;

// For generic usage (with default record filters)
export type PaginationParams<T extends z.ZodTypeAny = any> = InferPaginationParams<T>;
